import { useMemo } from 'react';
import { IconCircleCheck } from '@tabler/icons-react';

export interface LegalDocumentProps {
  /** Sanitised HTML (run stripUnsafeHtml first). The wording is shown exactly as written. */
  html: string;
  /** Plain-language summary lines shown first ("The short version"). Empty = no summary card. */
  summary?: string[];
  summaryTitle?: string;
}

interface TocItem { id: string; text: string }

function textOf(fragment: string): string {
  try {
    return (new DOMParser().parseFromString(fragment, 'text/html').body.textContent ?? '').trim();
  } catch {
    return fragment.replace(/<[^>]+>/g, '').trim();
  }
}

/** Adds ids to the section headings (h2 / h3) so a contents list can link to them. Markup only. */
function withAnchors(html: string): { html: string; toc: TocItem[] } {
  const toc: TocItem[] = [];
  let n = 0;
  const out = html.replace(/<h([23])(\s[^>]*)?>([\s\S]*?)<\/h\1>/gi, (match, level: string, attrs: string | undefined, inner: string) => {
    const existing = /\sid=["']([^"']+)["']/i.exec(attrs ?? '');
    n += 1;
    const id = existing ? existing[1] : `section-${n}`;
    const text = textOf(inner);
    if (text) toc.push({ id, text });
    return existing ? match : `<h${level}${attrs ?? ''} id="${id}">${inner}</h${level}>`;
  });
  return { html: out, toc };
}

/**
 * A long legal text made readable on a phone: an optional plain-language summary card, a
 * contents list with 48 px links to each section, then the full text at 16 px. The legal
 * wording itself is never changed; only ids are added to its headings.
 */
export function LegalDocument({ html, summary, summaryTitle = 'The short version' }: LegalDocumentProps) {
  const { html: anchored, toc } = useMemo(() => withAnchors(html), [html]);
  const lines = (summary ?? []).map((s) => s.trim()).filter(Boolean);
  return (
    <div className="rides-stack">
      {lines.length > 0 && (
        <section className="rides-card rides-sectioncard" aria-labelledby="rides-legal-summary">
          <div className="head"><h2 id="rides-legal-summary">{summaryTitle}</h2></div>
          <ul className="rides-shortlist">
            {lines.map((line, i) => (
              <li key={i}><IconCircleCheck size={20} aria-hidden="true" /><span>{line}</span></li>
            ))}
          </ul>
        </section>
      )}
      {toc.length >= 3 && (
        <nav className="rides-card rides-sectioncard flush" aria-labelledby="rides-legal-toc">
          <div className="head" style={{ marginTop: 12, marginBottom: 0 }}><h2 id="rides-legal-toc">On this page</h2></div>
          <ul className="rides-toc">
            {toc.map((item) => (
              <li key={item.id}><a href={`#${item.id}`}>{item.text}</a></li>
            ))}
          </ul>
        </nav>
      )}
      <div className="rides-card" style={{ padding: '18px 16px' }}>
        {/* Admin-authored HTML; <script>/<iframe>/on* handlers are removed by the caller (stripUnsafeHtml). */}
        <div className="rides-legal-doc" dangerouslySetInnerHTML={{ __html: anchored }} />
      </div>
    </div>
  );
}
